import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Clock, Users, Globe, Copy, Edit, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const TestPreview = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTestDetails();
  }, [testId]);

  const fetchTestDetails = async () => {
    try {
      const response = await axios.get(`/instructor/tests`);
      const foundTest = response.data.find(t => t.id === testId);
      if (foundTest) {
        setTest(foundTest);
      } else {
        toast.error('Test not found');
        navigate(-1);
      }
    } catch (error) {
      toast.error('Failed to load test');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const copyPublicLink = () => {
    const link = `${window.location.origin}/test/${test.public_link}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard!');
  };

  const renderFormattedText = (text) => {
    if (!text) return '';
    
    // Convert markdown-style formatting to HTML
    let formatted = text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') // Bold
      .replace(/\*(.+?)\*/g, '<em>$1</em>') // Italic
      .replace(/__(.+?)__/g, '<u>$1</u>') // Underline
      .replace(/\n/g, '<br/>'); // Line breaks
    
    return <div dangerouslySetInnerHTML={{ __html: formatted }} />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-main-gradient flex items-center justify-center">
        <div className="text-slate-600">Loading test...</div>
      </div>
    );
  }

  if (!test) {
    return null;
  }

  return (
    <div className="min-h-screen bg-main-gradient">
      {/* Header */}
      <div className="header-gradient">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-4 gap-3">
            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <Button
                onClick={() => navigate(-1)}
                variant="outline"
                size="sm"
                className="text-white border-white hover:bg-white hover:text-purple-600"
              >
                <ArrowLeft className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <h1 className="text-base sm:text-xl font-bold text-white">Test Preview</h1>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                className="text-white border-white hover:bg-white hover:text-purple-600 flex-1 sm:flex-initial"
              >
                <Edit className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Edit</span>
              </Button>
              {test.status === 'draft' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-400 border-red-400 hover:bg-red-500 hover:text-white flex-1 sm:flex-initial"
                >
                  <Trash2 className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Delete</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Test Info Card */}
        <Card className="glass-card mb-6">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="flex-1 min-w-0">
                <CardTitle className="text-xl sm:text-2xl text-slate-700 mb-3 break-words">{test.title}</CardTitle>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-slate-500">
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-1 flex-shrink-0" />
                    <span className="break-words">{test.duration_minutes} minutes</span>
                  </div>
                  <div className="flex items-center">
                    <Users className="w-4 h-4 mr-1 flex-shrink-0" />
                    <span className="break-words">{test.assigned_batches?.length || 0} batch(es), {test.assigned_students?.length || 0} student(s)</span>
                  </div>
                  {test.is_public && (
                    <div className="flex items-center text-blue-600">
                      <Globe className="w-4 h-4 mr-1 flex-shrink-0" />
                      <span>Public</span>
                    </div>
                  )}
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-semibold self-start ${
                test.status === 'active' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-yellow-100 text-yellow-700'
              }`}>
                {test.status.toUpperCase()}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {test.is_public && test.public_link && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-blue-900 mb-1">Public Test Link</p>
                    <code className="text-xs sm:text-sm text-blue-700 break-all block">
                      {window.location.origin}/test/{test.public_link}
                    </code>
                  </div>
                  <Button
                    onClick={copyPublicLink}
                    className="btn-primary w-full sm:w-auto"
                    size="sm"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Link
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Task 1 */}
        <Card className="glass-card mb-6">
          <CardHeader>
            <CardTitle className="text-lg text-slate-700">Task 1 (150 words minimum)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="text-slate-600 leading-relaxed">
                {renderFormattedText(test.task1_prompt)}
              </div>
              {test.task1_image && (
                <div className="border rounded-lg p-4 bg-slate-50">
                  <img
                    src={test.task1_image}
                    alt="Task 1 visual"
                    style={{
                      width: test.task1_image_width ? `${test.task1_image_width}px` : 'auto',
                      height: test.task1_image_height ? `${test.task1_image_height}px` : 'auto',
                      maxWidth: '100%',
                      objectFit: 'contain'
                    }}
                    className="mx-auto"
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Task 2 */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-lg text-slate-700">Task 2 (250 words minimum)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-slate-600 leading-relaxed">
              {renderFormattedText(test.task2_prompt)}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TestPreview;
